// Distance from the viewer to a venue, computed entirely in the browser.
//
// The user's coordinates deliberately never reach the server. Venue coordinates
// are public data and already travel with the fixture list, so the subtraction
// can happen here — which means no routing API, no per-request cost, and no
// location data to store or leak.
//
// Straight-line distance understates a real drive, so it is labelled as such in
// the UI rather than presented as travel time.

const KEY = 'leagueapp.lastLocation';

// A remembered position is only reused for a day; people travel, and a stale
// origin produces silently wrong distances.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const EARTH_RADIUS_MI = 3958.8;

export function haversineMiles(lat1, lon1, lat2, lon2) {
  if ([lat1, lon1, lat2, lon2].some((v) => typeof v !== 'number' || Number.isNaN(v))) {
    return null;
  }
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_MI * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function formatMiles(miles) {
  if (miles == null) return null;
  if (miles < 1) return '<1 mi';
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles)} mi`;
}

// Every localStorage access is guarded: private-mode browsers and blocked site
// data can throw on read as well as write.
export function readLocation() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.lat !== 'number' || typeof parsed.lon !== 'number') {
      return null;
    }
    if (!parsed.at || Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

export function writeLocation(lat, lon) {
  try {
    // Rounded to about 100m. Enough for a distance that is displayed to the
    // nearest mile, and avoids keeping a precise home address on disk.
    window.localStorage.setItem(KEY, JSON.stringify({
      lat: Math.round(lat * 1000) / 1000,
      lon: Math.round(lon * 1000) / 1000,
      at: Date.now(),
    }));
    return true;
  } catch (e) {
    return false;
  }
}

export function clearLocation() {
  try {
    window.localStorage.removeItem(KEY);
    return true;
  } catch (e) {
    return false;
  }
}

export function locationAvailable() {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

/**
 * Ask the browser for the viewer's position.
 *
 * Only ever called from an explicit user action — an unprompted permission
 * dialog on page load is the fastest way to get permanently denied.
 */
export function requestLocation() {
  return new Promise((resolve, reject) => {
    if (!locationAvailable()) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        writeLocation(latitude, longitude);
        resolve({ lat: latitude, lon: longitude });
      },
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 },
    );
  });
}
